import { SignUpSource } from '@/constants';
import { umsFetcher } from '@/fetchers/umsAPI/base.fetchers';
import type {
  AddEmailAccountResponse,
  TListEmailSyncAccountsDTO,
  ToggleEmailSyncAccountResponse,
} from '@/modules/pmt/interfaces/tracking/emailSync.interfaces';
import type { CodeResponse } from '@react-oauth/google';

type TListEmailSyncAccounts = () => Promise<TListEmailSyncAccountsDTO | undefined>;

export const listEmailSyncAccounts: TListEmailSyncAccounts = async () => {
  try {
    const response = await umsFetcher.get('email-accounts');
    if (response.ok) {
      return await response.json();
    }
    return Promise.reject(response);
  } catch (err) {
    console.error('Network error');
    throw err;
  }
};

type TToggleEmailSyncAccount = (args: {
  emailAccountId: string;
  realtimeSyncActivated: boolean;
  code?: string;
  redirectUri?: string;
}) => Promise<ToggleEmailSyncAccountResponse | undefined>;

export const toggleEmailSyncAccount: TToggleEmailSyncAccount = async ({
  emailAccountId,
  realtimeSyncActivated,
  code,
  redirectUri,
}) => {
  try {
    const response = await umsFetcher.patch(`email-accounts/${emailAccountId}`, {
      realtime_sync_activated: realtimeSyncActivated,
      code,
      redirect_uri: redirectUri,
    });

    const data = await response.json();
    const dataWithStatus = { ...data, status: response.status };
    return dataWithStatus;
  } catch (err) {
    console.error('Network error');
    throw err;
  }
};

type CountryConfig = (args: { countryCode?: string }) => Promise<boolean | undefined>;

export const getCountryConfig: CountryConfig = async ({ countryCode }) => {
  try {
    const res = await umsFetcher.get(`emailsync/country-config/${countryCode}`, { skipAuth: true });
    const data = await res.json();
    return data?.is_allowed;
    // biome-ignore lint/suspicious/noExplicitAny: catch clause requires any to access error properties
  } catch (err: any) {
    console.error('get country config ', err);
  }
};

export type AddEmailPayload = {
  code: CodeResponse['code'];
  signUpSource: SignUpSource;
  newsletterOptIn: boolean;
  scope: string;
  country?: string;
  userId?: string;
  redirectUri: string;
};

export const addEmailAccount = async ({
  code,
  signUpSource = SignUpSource.ConsumerWeb,
  newsletterOptIn = false,
  country,
  redirectUri,
}: AddEmailPayload): Promise<AddEmailAccountResponse> => {
  try {
    const response = await umsFetcher.post('email-accounts/add', {
      code,
      sign_up_source: signUpSource,
      newsletter_opt_in: newsletterOptIn,
      country,
      redirect_uri: redirectUri,
    });

    if (response.ok) {
      return await response.json();
    }

    const errorText = await response.text();
    throw JSON.parse(errorText);
  } catch (error) {
    console.error(error);
    throw error;
  }
};
